import { useState, useEffect, useRef } from 'react'
import { reportError } from 'utils/error'

interface UseVideoFrameExtractionProps {
  videoUrl: string
  videoDuration: number
}

interface UseVideoFrameExtractionReturn {
  frames: string[]
  isLoadingFrames: boolean
  hasDefaultAudio: boolean
  videoRef: React.RefObject<HTMLVideoElement>
}

let sharedCanvas: HTMLCanvasElement | null = null

const extractFrame = (video: HTMLVideoElement, time: number, width: number, height: number): Promise<string> => {
  return new Promise((resolve) => {
    if (!sharedCanvas) {
      sharedCanvas = document.createElement('canvas')
    }
    const canvas = sharedCanvas
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) return

    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
      resolve(canvas.toDataURL('image/jpeg', 0.8))
    }

    video.addEventListener('seeked', onSeeked)
    video.currentTime = time
  })
}

export const useVideoFrameExtraction = ({
  videoUrl,
  videoDuration,
}: UseVideoFrameExtractionProps): UseVideoFrameExtractionReturn => {
  const [frames, setFrames] = useState<string[]>([])
  const [isLoadingFrames, setIsLoadingFrames] = useState(true)
  const [hasDefaultAudio, setHasDefaultAudio] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const extractVideoFrames = async () => {
      if (!videoRef.current || !videoDuration) return

      setIsLoadingFrames(true)
      const video = videoRef.current
      const frames: string[] = []
      // Extract frames at ~1 second intervals (minimum 5 frames, capped at 10 for performance)
      const targetFrameCount = Math.min(10, Math.max(5, Math.ceil(videoDuration)))

      try {
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            // Check if video has audio after a short delay to ensure audio tracks are detected
            setTimeout(() => {
              const hasAudio =
                (video as any).mozHasAudio ||
                Boolean((video as any).webkitAudioDecodedByteCount) ||
                Boolean((video as any).audioTracks && (video as any).audioTracks.length)

              setHasDefaultAudio(hasAudio)
            }, 500)
            resolve(null)
          }
        })

        const videoAspectRatio = video.videoWidth / video.videoHeight
        const frameHeight = 100
        const frameWidth = frameHeight * videoAspectRatio

        for (let i = 0; i < targetFrameCount; i += 1) {
          const t = (i / (targetFrameCount - 1)) * videoDuration
          const frame = await extractFrame(video, Math.min(t, videoDuration - 0.01), frameWidth, frameHeight)
          frames.push(frame)
        }

        setFrames(frames)
      } catch (error) {
        reportError(error)
      } finally {
        setIsLoadingFrames(false)
      }
    }

    extractVideoFrames()
  }, [videoUrl, videoDuration])

  return {
    frames,
    isLoadingFrames,
    hasDefaultAudio,
    videoRef,
  }
}
